import { Contract } from '@/app/types/proposals';
import { isAddress } from 'ethers';

if (!process.env.NEXT_PUBLIC_GOVERNOR_ADDRESS) {
  throw new Error('NEXT_PUBLIC_GOVERNOR_ADDRESS environment variable is not set');
} else if (!isAddress(process.env.NEXT_PUBLIC_GOVERNOR_ADDRESS)) {
  throw new Error('NEXT_PUBLIC_GOVERNOR_ADDRESS is not a valid Ethereum address');
}
if (!process.env.NEXT_PUBLIC_TIMELOCK_ADDRESS) {
  throw new Error('NEXT_PUBLIC_TIMELOCK_ADDRESS environment variable is not set');
} else if (!isAddress(process.env.NEXT_PUBLIC_TIMELOCK_ADDRESS)) {
  throw new Error('NEXT_PUBLIC_TIMELOCK_ADDRESS is not a valid Ethereum address');
}
if (!process.env.NEXT_PUBLIC_TOKEN_ADDRESS) {
  throw new Error('NEXT_PUBLIC_TOKEN_ADDRESS environment variable is not set');
} else if (!isAddress(process.env.NEXT_PUBLIC_TOKEN_ADDRESS)) {
  throw new Error('NEXT_PUBLIC_TOKEN_ADDRESS is not a valid Ethereum address');
}

export const proposalFlow: {
  proposal: {
    [key: string]: {
      type: string;
      name: string;
      contracts: Contract[];
    };
  };
} = {
  proposal: {
    constitutionalUpgrades: {
      type: 'constitutionalUpgrades',
      name: 'Constitutional Upgrades',
      contracts: [
        {
          name: 'Governor Contract',
          address: process.env.NEXT_PUBLIC_GOVERNOR_ADDRESS,
          methods: [
            {
              name: 'setQuorumIncreaseNumerator',
              show: 'Set Quorum Increase Numerator',
              description:
                '"SetQuorumIncreaseNumerator" allows the DAO to adjust the quorum increase numerator, which determines the percentage increase in quorum required for a proposal to pass.',
              inputs: [
                {
                  id: '',
                  internalType: 'uint256',
                  name: '_newIncreaseNumerator',
                  type: 'uint256',
                  show: 'New Increase Numerator',
                  value: '',
                  internalValue: '',
                },
              ],
            },
            {
              name: 'fireCanceller',
              show: 'Fire Canceller',
              description:
                '"FireCanceller" removes a canceller from the DAO, preventing them from vetoing proposals.',
              inputs: [
                {
                  id: '',
                  internalType: 'address',
                  name: 'fired',
                  type: 'address',
                  show: 'Fired',
                  value: '',
                  internalValue: '',
                },
              ],
            },
            {
              name: 'relay',
              show: 'Relay',
              description:
                '"Relay" allows the DAO to relay a transaction or function call to an arbitrary target. This can be used to recover tokens or Ether that was sent to the governor contract by mistake.',
              inputs: [
                {
                  id: '',
                  internalType: 'address',
                  name: 'target',
                  type: 'address',
                  show: 'Target',
                  value: '',
                  internalValue: '',
                },
                {
                  id: '',
                  internalType: 'uint256',
                  name: 'value',
                  type: 'uint256',
                  show: 'Value',
                  value: '',
                  internalValue: '',
                },
                {
                  id: '',
                  internalType: 'bytes',
                  name: 'data',
                  type: 'bytes',
                  show: 'Data',
                  value: '',
                  internalValue: '',
                },
              ],
            },
            {
              name: 'updateQuorumNumerator',
              show: 'Update Quorum Numerator',
              description:
                '"UpdateQuorumNumerator" allows the DAO to update the quorum numerator, which determines the percentage of votes required to pass a proposal.',
              inputs: [
                {
                  id: '',
                  internalType: 'uint256',
                  name: 'newQuorumNumerator',
                  type: 'uint256',
                  show: 'New Quorum Numerator',
                  value: '',
                  internalValue: '',
                },
              ],
            },
            {
              name: 'setVotingDelay',
              show: 'Set Voting Delay',
              description:
                '"SetVotingDelay" allows the DAO to update the voting delay, which determines the time between a proposal being created and the voting period beginning.',
              inputs: [
                {
                  id: '',
                  internalType: 'uint48',
                  name: 'newVotingDelay',
                  type: 'uint48',
                  show: 'New Voting Delay',
                  value: '',
                  internalValue: '',
                },
              ],
            },
            {
              name: 'setVotingPeriod',
              show: 'Set Voting Period',
              description:
                '"SetVotingPeriod" allows the DAO to update the voting period, which determines the time between a proposal being created and the voting period ending.',
              inputs: [
                {
                  id: '',
                  internalType: 'uint32',
                  name: 'newVotingPeriod',
                  type: 'uint32',
                  show: 'New Voting Period',
                  value: '',
                  internalValue: '',
                },
              ],
            },
            {
              name: 'setProposalThreshold',
              show: 'Set Proposal Threshold',
              description:
                '"SetProposalThreshold" allows the DAO to update the proposal threshold, which determines the minimum number of votes required to pass a proposal.',
              inputs: [
                {
                  id: '',
                  internalType: 'uint256',
                  name: 'newProposalThreshold',
                  type: 'uint256',
                  show: 'New Proposal Threshold',
                  value: '',
                  internalValue: '',
                },
              ],
            },
          ],
        },
      ],
    },
    fundingRequests: {
      type: 'fundingRequests',
      name: 'Funding Requests',
      contracts: [
        {
          name: 'Token Contract',
          address: process.env.NEXT_PUBLIC_TOKEN_ADDRESS,
          methods: [
            {
              name: 'approve',
              show: 'Approve Spender',
              description:
                '"Approve" implements the ERC-20 standard approval mechanism, allowing a specified spender to manage a set amount of tokens on behalf of the token owner.',
              inputs: [
                {
                  id: '',
                  internalType: 'address',
                  name: 'spender',
                  type: 'address',
                  show: 'Spender',
                  value: '',
                  internalValue: '',
                },
                {
                  id: '',
                  internalType: 'uint256',
                  name: 'value',
                  type: 'uint256',
                  show: 'Karrat amount',
                  value: '',
                  internalValue: '',
                  variant: 'wei',
                },
              ],
            },
            {
              name: 'permit',
              show: 'Permit',
              description:
                '"Permit" provides an EIP-2612 compliant way for token holders to approve token spending through off-chain signatures, eliminating the need for a separate approval transaction.',
              inputs: [
                {
                  id: '',
                  internalType: 'address',
                  name: 'owner',
                  type: 'address',
                  show: 'Owner',
                  value: '',
                  internalValue: '',
                },
                {
                  id: '',
                  internalType: 'address',
                  name: 'spender',
                  type: 'address',
                  show: 'Spender',
                  value: '',
                  internalValue: '',
                },
                {
                  id: '',
                  internalType: 'uint256',
                  name: 'value',
                  type: 'uint256',
                  show: 'Karrat amount',
                  value: '',
                  internalValue: '',
                  variant: 'wei',
                },
                {
                  id: '',
                  internalType: 'uint256',
                  name: 'deadline',
                  type: 'uint256',
                  show: 'Deadline',
                  value: '',
                  internalValue: '',
                },
                {
                  id: '',
                  internalType: 'uint8',
                  name: 'v',
                  type: 'uint8',
                  show: 'V',
                  value: '',
                  internalValue: '',
                },
                {
                  id: '',
                  internalType: 'bytes32',
                  name: 'r',
                  type: 'bytes32',
                  show: 'R',
                  value: '',
                  internalValue: '',
                },
                {
                  id: '',
                  internalType: 'bytes32',
                  name: 's',
                  type: 'bytes32',
                  show: 'S',
                  value: '',
                  internalValue: '',
                },
              ],
            },
            {
              name: 'transfer',
              show: 'Transfer Funds',
              description:
                '"Transfer" enables the direct movement of tokens from the contract to a specified recipient address.',
              inputs: [
                {
                  id: '',
                  internalType: 'address',
                  name: 'to',
                  type: 'address',
                  show: 'To',
                  value: '',
                  internalValue: '',
                },
                {
                  id: '',
                  internalType: 'uint256',
                  name: 'value',
                  type: 'uint256',
                  show: 'Karrat amount',
                  value: '',
                  internalValue: '',
                  variant: 'wei',
                },
              ],
            },
            {
              name: 'transferFrom',
              show: 'Transfer Funds From',
              description:
                '"TransferFrom" facilitates the transfer of tokens between addresses when the sender has been previously approved to spend on behalf of the token owner.',
              inputs: [
                {
                  id: '',
                  internalType: 'address',
                  name: 'from',
                  type: 'address',
                  show: 'From',
                  value: '',
                  internalValue: '',
                },
                {
                  id: '',
                  internalType: 'address',
                  name: 'to',
                  type: 'address',
                  show: 'To',
                  value: '',
                  internalValue: '',
                },
                {
                  id: '',
                  internalType: 'uint256',
                  name: 'value',
                  type: 'uint256',
                  show: 'Karrat amount',
                  value: '',
                  internalValue: '',
                  variant: 'wei',
                },
              ],
            },
          ],
        },
      ],
    },
    informational: {
      type: 'informational',
      name: 'Informational',
      contracts: [],
    },
  },
} as const;
